import React, { useEffect, useState } from 'react'
import { Button, Checkbox } from '@mui/material';


import { ICustomerLight } from '../../types/customer';

import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';

import "../../styles/entity/customerClosing.scss";
import { get } from 'axios';

export interface ICustomerClosingTableProps {
	data?: ICustomerLight[];
	onCustomerSelectionChange?: (selectedCustomerIds: number[]) => void;
}

interface ISelectableCustomer extends ICustomerLight {
	isSelected: boolean;
}

const CustomerClosingTable: React.FunctionComponent<ICustomerClosingTableProps> = ({ data, onCustomerSelectionChange }) => {
	const [dataView, setDataView] = useState<ISelectableCustomer[]>([]);
	const [sortField, setSortField] = useState("name");
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
	const [transformedData, setTransformedData] = useState<ISelectableCustomer[] | undefined>(undefined);

	useEffect(() => {
		if (!Array.isArray(data)) {
			setTransformedData([]);
			return;
		}

		let newData = [...data].filter(item => item.status == "Active").map(item => ({ ...item, isSelected: false } as ISelectableCustomer));

		// Make sure the closing dates are actually dates and not strings
		newData.forEach((item) => {
			if (typeof item.closedThroughDate == "string") {
				item.closedThroughDate = new Date(item.closedThroughDate as string);
			}
		});

		setTransformedData(newData);
		onCustomerSelectionChange?.([]);
	}, [data])

	useEffect(() => {
		if (transformedData) {
			setIsLoadingCustomers(true);

			let newData = transformedData.filter((item) => {
				if (item.status != "Active") {
					return false;
				}
				return true;
			}).slice();

			if (sortField) {
				switch (sortField) {
					case "brand": {
						newData.sort((a, b) => UtilityService.compareStrings(a.brand, b.brand));
						break;
					}
					case "state": {
						newData.sort((a, b) => UtilityService.compareStrings(a.state, b.state));
						break;
					}
					case "city": {
						newData.sort((a, b) => UtilityService.compareStrings(a.city, b.city));
						break;
					}
					case "closed-through": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.closedThroughDate.getTime(), b.closedThroughDate.getTime()));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
			setIsLoadingCustomers(false);
		}
	}, [transformedData, sortField, sortAscending])

	const getSelectedCustomerIds = (): number[] => {
		return (transformedData ?? []).filter(item => item.isSelected).map(item => +item.id);
	}

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handleCustomerRowMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>,customer: ISelectableCustomer) => {
		if (event.button != 0) return; // Only handle left click
		customer.isSelected = !customer.isSelected;
		setTransformedData(transformedData?.slice());
		onCustomerSelectionChange?.(getSelectedCustomerIds());
	}

	const handleMassSelectCheckboxClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (getSelectedCustomerIds().length == transformedData?.length) {
			clearSelection();
		} else {
			selectAll();
		}
	}

	const selectAll = () => {
		transformedData?.forEach((item) => {
			item.isSelected = true;
		});
		setTransformedData(transformedData?.slice());
		onCustomerSelectionChange?.(getSelectedCustomerIds());
	}

	const clearSelection = () => {
		transformedData?.forEach((item) => {
			item.isSelected = false;
		});
		setTransformedData(transformedData?.slice());
		onCustomerSelectionChange?.(getSelectedCustomerIds());
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-select">
					{<Checkbox name={`mass-select`} checked={getSelectedCustomerIds().length == transformedData?.length} onClick={handleMassSelectCheckboxClick}></Checkbox>}
				</div>
				<div className="col-name">
					<p onClick={() => handleHeaderClick("name")}>
						Name
						{" "}
						{(sortField == "name" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "name" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-brand">
					<p onClick={() => handleHeaderClick("brand")}>
						Brand
						{" "}
						{(sortField == "brand" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "brand" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-state">
					<p onClick={() => handleHeaderClick("state")}>
						State
						{" "}
						{(sortField == "state" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "state" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-city">
					<p onClick={() => handleHeaderClick("city")}>
						City
						{" "}
						{(sortField == "city" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "city" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-closed-through">
					<p onClick={() => handleHeaderClick("closed-through")}>
						Closed Through
						{" "}
						{(sortField == "closed-through" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "closed-through" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
			</div>
			<Spinner dependency={isLoadingCustomers} text="Loading active customers...">
				<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.map((item, index) => (
								<div className="row" key={item.id} onMouseDown={(e) => handleCustomerRowMouseDown(e, item)}>
									<div className="col-select">{<Checkbox name={`select-customer-${item.id}`} checked={item.isSelected} onClick={(e) => e.preventDefault()}></Checkbox>}</div>
									<div className="col-name"><p>{item.name ? item.name : <span className="inactive">(None)</span>}</p></div>
									<div className="col-brand"><p>{item.brand ? item.brand : <span className="inactive">(None)</span>}</p></div>
									<div className="col-state"><p>{item.state ? item.state : <span className="inactive">(None)</span>}</p></div>
									<div className="col-city"><p>{item.city ? item.city : <span className="inactive">(None)</span>}</p></div>
									<div className="col-closed-through"><p>{item.closedThroughDate ? item.closedThroughDate.toLocaleDateString() : <span className="inactive">(None)</span>}</p></div>
								</div>
							))
						) :
						(
							<div className="no-results">There are no active customers to close.</div>
						)
					}
				</div>
			</Spinner>
		</div>
	)
}

export default CustomerClosingTable;